import { modelOptions, post, prop } from "@typegoose/typegoose";
import { z } from "zod";
import { getZodSchemaFromClass, validate } from "../../zod-decorator/index.js";
import { addSchemaKeyHook, getDefaultSchemaOptions, SchemaBase, SchemaConstructorArgs } from "../schemaBase.js";
import { VertexInfoFieldsWithDefaults, VertexInfoSchemaTypes } from "../types/vertexInfo.js";
import { DtoValidatorTypes } from "../validatableClass.js";

const SCHEMA_KEY = "VertexInfo" as const;

@modelOptions({ schemaOptions: { ...getDefaultSchemaOptions(SCHEMA_KEY) } })
@post<SchemaBase<VertexInfo>>(...addSchemaKeyHook(SCHEMA_KEY))
export class VertexInfo extends SchemaBase<VertexInfo, VertexInfoFieldsWithDefaults> {
    static readonly schemaKey = SCHEMA_KEY;

    @validate({ schemaKey: SCHEMA_KEY })
    readonly schemaKey = SCHEMA_KEY;

    // Legacy: some DB records have null lat/lng/depth (created before the VertexInfo default was enforced).
    // .nullish() required here to allow the response validator to handle legacy values
    @validate({ zodValidator: z.number().nullish() })
    @prop({ type: Number, required: false })
    public lat?: number;

    @validate({ zodValidator: z.number().nullish() })
    @prop({ type: Number, required: false })
    public lng?: number;

    /**
     * in meters
     * @type {number}
     */
    @validate({ zodValidator: z.number().nullish() })
    @prop({ type: Number, required: false })
    public depth?: number;

    static getters = {
        positionHash: function createVertexInfoPositionHash(vertexInfo: VertexInfo): number {
            const SignificantDigits = 300000;
            const coords = [vertexInfo.lat, vertexInfo.lng];
            let response = 0;
            for (let i = 0; i < 2; i++) {
                coords[i] = (coords[i] * SignificantDigits) % SignificantDigits;
                response += Math.floor(coords[i]) * SignificantDigits ** i;
            }
            if (vertexInfo.depth) {
                const hashDepth = (vertexInfo.depth * 100) % 10000;
                response += SignificantDigits ** 2 * hashDepth;
            }
            return response;
        },
    } as const;

    static utilities = {
        /**
         * Supports a legacy constructor style. Should use normal constructor going forward.
         * @param lat
         * @param lng
         * @param depth
         */
        createFromLatLng: (lat: number, lng: number, depth = 0) =>
            new VertexInfo({
                lat: lat,
                lng: lng,
                depth: depth,
            }),
        /**
         * Calculates the 3D distance between two VertexInfo points using the Haversine formula
         * for surface distance and Euclidean distance for depth difference.
         *
         * Not that useful, but need 2 utility functions at minimum for the typescript to work.
         * @param vertex1 First vertex
         * @param vertex2 Second vertex
         * @returns Distance in meters
         */
        getDistanceInMeters: (vertex1: VertexInfo, vertex2: VertexInfo): number => {
            const EARTH_RADIUS_METERS = 6371000; // Earth's radius in meters

            // Convert latitude and longitude from degrees to radians
            const lat1Rad = (vertex1.lat * Math.PI) / 180;
            const lat2Rad = (vertex2.lat * Math.PI) / 180;
            const deltaLatRad = ((vertex2.lat - vertex1.lat) * Math.PI) / 180;
            const deltaLngRad = ((vertex2.lng - vertex1.lng) * Math.PI) / 180;

            // Haversine formula for surface distance
            const a =
                Math.sin(deltaLatRad / 2) * Math.sin(deltaLatRad / 2) +
                Math.cos(lat1Rad) * Math.cos(lat2Rad) * Math.sin(deltaLngRad / 2) * Math.sin(deltaLngRad / 2);
            const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
            const surfaceDistance = EARTH_RADIUS_METERS * c;

            // Calculate depth difference
            const depthDifference = Math.abs((vertex2.depth || 0) - (vertex1.depth || 0));

            // Calculate 3D distance using Pythagorean theorem
            return Math.sqrt(surfaceDistance ** 2 + depthDifference ** 2);
        },
    } as const;

    static dtoValidators: DtoValidatorTypes<VertexInfoSchemaTypes>;

    constructor(properties?: SchemaConstructorArgs<VertexInfo, VertexInfoFieldsWithDefaults>) {
        super(properties);
        Object.assign(this, properties);
    }
}

const VertexInfoSchema = getZodSchemaFromClass(VertexInfo);
VertexInfo.dtoValidators = VertexInfoSchema as unknown as DtoValidatorTypes<VertexInfoSchemaTypes>;
